import { isJsonObject, jsonObject } from '@/types/json.types'
/**
 * Mock tenant service — backed by the in-memory `mockTenants` / `mockSubscriptions`
 * fixtures.
 *
 * Updates persist for the lifetime of the page (the factory memoizes the
 * instance), so the Organization settings screen survives in-session reloads of
 * its own data even in mock mode. The auth user's `tenantId` rarely matches a
 * fixture id, so lookups fall back to the first mock tenant.
 */

import { mockTenants, mockSubscriptions } from '@/data/mockTenants'
import { mockDelay } from '@/services/factory'
import type { TenantService } from './tenant.service'
import type {
  BillingCycle,
  BillingOverview,
  DowngradeBlocker,
  EffectiveEntitlements,
  PlanCode,
  PlanCommandInput,
  Subscription,
  SubscriptionCommandResult,
  SubscriptionInvoice,
  SubscriptionLifecycleEvent,
  Tenant,
  UpdateTenantInput,
} from '@/types/tenant.types'

interface MockCheckoutRecord {
  invoice: SubscriptionInvoice
  subscription: Subscription
}

const checkoutRegistry = new Map<string, MockCheckoutRecord>()
const demoPrices: Record<Exclude<PlanCode, 'trial'>, Record<BillingCycle, number>> = {
  starter: { monthly: 99000, yearly: 990000 },
  professional: { monthly: 299000, yearly: 2990000 },
  enterprise: { monthly: 999000, yearly: 9990000 },
}

export function getMockSubscriptionInvoice(invoiceId: string): SubscriptionInvoice | undefined {
  const invoice = checkoutRegistry.get(invoiceId)?.invoice
  return invoice ? structuredClone(invoice) : undefined
}

export function settleMockSubscriptionInvoice(invoiceId: string, providerPaymentId: string): void {
  const record = checkoutRegistry.get(invoiceId)
  if (!record) throw new Error('Subscription invoice not found')
  const now = new Date().toISOString()
  record.invoice.status = 'paid'
  record.invoice.providerPaymentId = providerPaymentId
  record.invoice.paidAt = now
  record.invoice.updatedAt = now
  record.subscription.planCode = record.invoice.requestedPlanCode
  record.subscription.billingCycle = record.invoice.requestedBillingCycle
  record.subscription.status = 'active'
  record.subscription.pendingPlanCode = undefined
  record.subscription.pendingBillingCycle = undefined
  record.subscription.pendingChangeStatus = undefined
  record.subscription.pendingChangeDetails = {}
  record.subscription.lastPaymentAt = now
  record.subscription.updatedAt = now
}

export function createMockTenantService(): TenantService {
  // Session-local store seeded from the fixtures (deep-copied so edits don't
  // leak into the shared module-level arrays consumed elsewhere).
  const store: Tenant[] = mockTenants.map(t => structuredClone(t))
  const subscriptions: Subscription[] = mockSubscriptions.map(s => structuredClone(s))
  const invoices: SubscriptionInvoice[] = []
  const history: SubscriptionLifecycleEvent[] = []

  const planLimits: Record<PlanCode, Record<string, number>> = {
    trial: { maxUsers: 3, maxClients: 25, maxBranches: 1, maxStorageBytes: 1024 ** 3 },
    starter: { maxUsers: 3, maxClients: 50, maxBranches: 1, maxStorageBytes: 5 * 1024 ** 3 },
    professional: {
      maxUsers: 15,
      maxClients: 500,
      maxBranches: 3,
      maxStorageBytes: 25 * 1024 ** 3,
    },
    enterprise: {
      maxUsers: 9999,
      maxClients: 99999,
      maxBranches: 99,
      maxStorageBytes: 500 * 1024 ** 3,
    },
  }
  const planOrder: Record<PlanCode, number> = {
    trial: 0,
    starter: 1,
    professional: 2,
    enterprise: 3,
  }
  const usage = { users: 8, clients: 234, branches: 1, storageBytes: 12.5 * 1024 ** 3 }

  function resolve(tenantId: string): Tenant {
    return store.find(t => t.tenantId === tenantId) ?? store[0]
  }

  function resolveSubscription(tenantId: string): Subscription | undefined {
    return subscriptions.find(s => s.tenantId === tenantId) ?? subscriptions[1] ?? subscriptions[0]
  }

  function blockersFor(planCode: PlanCode): DowngradeBlocker[] {
    const limits = planLimits[planCode]
    const pairs = [
      ['users', usage.users, limits.maxUsers, 'active user seats'],
      ['clients', usage.clients, limits.maxClients, 'retained clients'],
      ['branches', usage.branches, limits.maxBranches, 'active branches'],
      ['storage_bytes', usage.storageBytes, limits.maxStorageBytes, 'stored document bytes'],
    ] as const
    return pairs
      .filter(([, used, limit]) => used > limit)
      .map(([quotaKey, used, targetLimit, description]) => ({
        quotaKey,
        used,
        targetLimit,
        excess: used - targetLimit,
        description,
      }))
  }

  function addInvoice(
    sub: Subscription,
    purpose: SubscriptionInvoice['purpose'],
    planCode: PlanCode,
    billingCycle: BillingCycle
  ): SubscriptionInvoice {
    const now = new Date().toISOString()
    const totalMinor = planCode === 'trial' ? 0 : demoPrices[planCode][billingCycle]
    const invoice: SubscriptionInvoice = {
      invoiceId: `invoice-${Date.now()}-${invoices.length}`,
      invoiceNumber: `TH-DEMO-${String(invoices.length + 1).padStart(4, '0')}`,
      tenantId: sub.tenantId,
      subscriptionId: sub.subscriptionId,
      purpose,
      status: 'pending',
      requestedPlanCode: planCode,
      requestedBillingCycle: billingCycle,
      amountMinor: totalMinor,
      taxMinor: 0,
      totalMinor,
      currency: 'INR',
      dueAt: new Date(Date.now() + 86400000).toISOString(),
      createdAt: now,
      updatedAt: now,
    }
    invoices.unshift(invoice)
    checkoutRegistry.set(invoice.invoiceId, { invoice, subscription: sub })
    return invoice
  }

  function addHistory(sub: Subscription, eventType: string, idempotencyKey: string): void {
    history.unshift({
      eventId: `event-${Date.now()}-${history.length}`,
      tenantId: sub.tenantId,
      subscriptionId: sub.subscriptionId,
      eventType,
      source: 'owner',
      idempotencyKey,
      previousStatus: sub.status,
      newStatus: sub.status,
      details: {},
      createdAt: new Date().toISOString(),
    })
  }

  function command(
    sub: Subscription,
    invoice?: SubscriptionInvoice,
    downgradeBlockers: DowngradeBlocker[] = []
  ): SubscriptionCommandResult {
    return {
      subscription: structuredClone(sub),
      invoice: invoice ? structuredClone(invoice) : undefined,
      downgradeBlockers,
      idempotentReplay: false,
    }
  }

  return {
    async get(tenantId: string): Promise<Tenant> {
      await mockDelay(300)
      return structuredClone(resolve(tenantId))
    },

    async update(tenantId: string, patch: UpdateTenantInput): Promise<Tenant> {
      await mockDelay(400)
      const tenant = resolve(tenantId)
      for (const key of ['settings', 'firmAddress'] as const) {
        if (
          patch[key] &&
          Object.keys(patch[key]).length &&
          tenant[key] != null &&
          !isJsonObject(tenant[key])
        ) {
          throw new Error(`Recorded ${key} cannot be patched as an object`)
        }
      }
      if (patch.tenantName !== undefined) tenant.tenantName = patch.tenantName
      if (patch.firmEmail !== undefined) tenant.firmEmail = patch.firmEmail
      if (patch.firmPhone !== undefined) tenant.firmPhone = patch.firmPhone
      if (patch.firmGstin !== undefined) tenant.firmGstin = patch.firmGstin
      if (patch.firmPan !== undefined) tenant.firmPan = patch.firmPan
      if (patch.firmWebsite !== undefined) tenant.firmWebsite = patch.firmWebsite
      if (patch.logoUrl !== undefined) tenant.logoUrl = patch.logoUrl
      if (patch.firmAddress !== undefined && Object.keys(patch.firmAddress).length) {
        tenant.firmAddress = { ...jsonObject(tenant.firmAddress), ...patch.firmAddress }
      }
      if (patch.settings !== undefined && Object.keys(patch.settings).length) {
        tenant.settings = { ...jsonObject(tenant.settings), ...patch.settings }
      }
      tenant.updatedAt = new Date().toISOString()
      return structuredClone(tenant)
    },

    async getSubscription(tenantId: string): Promise<Subscription | null> {
      await mockDelay(200)
      const sub = resolveSubscription(tenantId)
      return sub ? structuredClone(sub) : null
    },

    async getEffectiveEntitlements(tenantId: string): Promise<EffectiveEntitlements> {
      await mockDelay(200)
      const sub = resolveSubscription(tenantId)
      const planCode = sub?.planCode ?? 'professional'
      const isProfessional = planCode === 'professional' || planCode === 'enterprise'
      const isEnterprise = planCode === 'enterprise'
      return {
        tenantId,
        planCode,
        planName: planCode.charAt(0).toUpperCase() + planCode.slice(1),
        subscriptionStatus: sub?.status ?? 'active',
        limits: {
          maxUsers: sub?.maxUsers ?? 15,
          maxClients: sub?.maxClients ?? 500,
          maxBranches: isEnterprise ? 99 : isProfessional ? 3 : 1,
          maxStorageBytes: (isEnterprise ? 500 : isProfessional ? 25 : 5) * 1024 ** 3,
          maxBulkItems: isEnterprise ? 5000 : isProfessional ? 500 : 0,
        },
        features: {
          basicReports: true,
          advancedReports: isProfessional,
          bulkOperations: isProfessional,
          apiAccess: isEnterprise,
          sso: isEnterprise,
          customBranding: isEnterprise,
        },
        usage: { users: 8, clients: 234, branches: 1, storageBytes: 12.5 * 1024 ** 3 },
        appliedOverrides: [],
        upgrade: {
          upgradeAvailable: !isEnterprise,
          recommendedPlan: isProfessional ? 'enterprise' : 'professional',
        },
      }
    },

    async getBillingOverview(tenantId: string): Promise<BillingOverview> {
      await mockDelay(200)
      const sub = resolveSubscription(tenantId)
      const pendingBlockers = sub?.pendingPlanCode ? blockersFor(sub.pendingPlanCode) : []
      return {
        subscription: sub ? structuredClone(sub) : null,
        usage: structuredClone(usage),
        currentLimits: structuredClone(planLimits[sub?.planCode ?? 'starter']),
        downgradeBlockers: pendingBlockers,
        invoices: structuredClone(invoices.filter(item => !sub || item.tenantId === sub.tenantId)),
        history: structuredClone(history.filter(item => !sub || item.tenantId === sub.tenantId)),
      }
    },

    async purchaseSubscription(
      tenantId: string,
      input: PlanCommandInput
    ): Promise<SubscriptionCommandResult> {
      await mockDelay(250)
      let sub = resolveSubscription(tenantId)
      if (!sub) {
        const now = new Date().toISOString()
        sub = {
          subscriptionId: `sub-${Date.now()}`,
          tenantId,
          planCode: input.planCode,
          status: 'pending',
          billingCycle: input.billingCycle,
          startsAt: now,
          createdAt: now,
          updatedAt: now,
        }
        subscriptions.push(sub)
      }
      sub.status = 'pending'
      sub.planCode = input.planCode
      sub.billingCycle = input.billingCycle
      const invoice = addInvoice(sub, 'purchase', input.planCode, input.billingCycle)
      addHistory(sub, 'subscription.purchase.requested', input.idempotencyKey)
      return command(sub, invoice)
    },

    async changeSubscriptionPlan(
      tenantId: string,
      input: PlanCommandInput
    ): Promise<SubscriptionCommandResult> {
      await mockDelay(250)
      const sub = resolveSubscription(tenantId)
      if (!sub) throw new Error('Subscription not found')
      const isDowngrade = planOrder[input.planCode] < planOrder[sub.planCode]
      const downgradeBlockers = isDowngrade ? blockersFor(input.planCode) : []
      sub.pendingPlanCode = input.planCode
      sub.pendingBillingCycle = input.billingCycle
      sub.pendingChangeStatus = isDowngrade
        ? downgradeBlockers.length
          ? 'blocked_usage'
          : 'scheduled'
        : 'awaiting_payment'
      sub.pendingChangeDetails = { downgradeBlockers }
      sub.updatedAt = new Date().toISOString()
      const invoice = isDowngrade
        ? undefined
        : addInvoice(
            sub,
            planOrder[input.planCode] > planOrder[sub.planCode] ? 'upgrade' : 'cycle_change',
            input.planCode,
            input.billingCycle
          )
      addHistory(sub, 'subscription.plan_change.requested', input.idempotencyKey)
      return command(sub, invoice, downgradeBlockers)
    },

    async renewSubscription(
      tenantId: string,
      input: { billingCycle?: BillingCycle; idempotencyKey: string }
    ): Promise<SubscriptionCommandResult> {
      await mockDelay(250)
      const sub = resolveSubscription(tenantId)
      if (!sub) throw new Error('Subscription not found')
      const cycle = input.billingCycle ?? sub.billingCycle
      if (!cycle) throw new Error('Select a billing cycle')
      const invoice = addInvoice(sub, 'renewal', sub.planCode, cycle)
      sub.pendingChangeStatus = 'awaiting_payment'
      addHistory(sub, 'subscription.renewal.requested', input.idempotencyKey)
      return command(sub, invoice)
    },

    async cancelSubscription(
      tenantId: string,
      input: { reason: string; idempotencyKey: string }
    ): Promise<SubscriptionCommandResult> {
      await mockDelay(250)
      const sub = resolveSubscription(tenantId)
      if (!sub) throw new Error('Subscription not found')
      sub.cancelAtPeriodEnd = true
      sub.cancelledAt = new Date().toISOString()
      addHistory(sub, 'subscription.cancellation.requested', input.idempotencyKey)
      return command(sub)
    },

    async reactivateSubscription(
      tenantId: string,
      input: { billingCycle?: BillingCycle; idempotencyKey: string }
    ): Promise<SubscriptionCommandResult> {
      await mockDelay(250)
      const sub = resolveSubscription(tenantId)
      if (!sub) throw new Error('Subscription not found')
      let invoice: SubscriptionInvoice | undefined
      if (sub.cancelAtPeriodEnd && ['active', 'trial', 'grace'].includes(sub.status)) {
        sub.cancelAtPeriodEnd = false
        sub.cancelledAt = undefined
      } else {
        sub.status = 'pending'
        const cycle = input.billingCycle ?? sub.billingCycle
        if (!cycle) throw new Error('Select a billing cycle')
        sub.billingCycle = cycle
        sub.pendingChangeStatus = 'awaiting_payment'
        invoice = addInvoice(sub, 'reactivation', sub.planCode, sub.billingCycle)
      }
      addHistory(sub, 'subscription.reactivation.requested', input.idempotencyKey)
      return command(sub, invoice)
    },
  }
}
